import React, { useState, useEffect, useContext } from 'react';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Button, Box } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { motion } from 'framer-motion';
import '../styles/css/WelcomeSlideshow.css';
import logoImage from '../styles/images/logo.png';
import { LanguageContext } from '../App';

const heroTextVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Dynamically import images from both orientation folders
const importAll = (r) => {
  try {
    return r.keys().map(r);
  } catch (err) {
    console.error("Error importing images:", err);
    return [];
  }
};

// Wrap in try-catch to prevent crashes if images can't be loaded
let portraitImages = [];
let landscapeImages = [];

const shuffleArray = (array) => {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

try {
  portraitImages = shuffleArray(importAll(require.context('../styles/images/slideshow/portrait', false, /\.webp$/)));
  landscapeImages = shuffleArray(importAll(require.context('../styles/images/slideshow/landscape', false, /\.webp$/)));
} catch (error) {
  console.error("Failed to load slideshow images:", error);
}

const WelcomeSlideshow = () => {
  const [mounted, setMounted] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isRtl, setIsRtl] = useState(false);
  const { language, translations } = useContext(LanguageContext);
  
  useEffect(() => {
    setMounted(true);
    
    const checkOrientation = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    
    const checkRtl = () => {
      const htmlDir = document.documentElement.getAttribute('dir');
      setIsRtl(htmlDir === 'rtl');
    };
    
    checkOrientation();
    checkRtl();
    
    window.addEventListener('resize', checkOrientation);
    
    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  // Select images based on orientation
  const images = isPortrait ? portraitImages : landscapeImages;

  const handleScrollToPortfolio = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Simple loader for initial render
  if (!mounted) {
    return <div className="slideshow-loading">Loading slideshow...</div>;
  }

  const whatsappHref = `https://wa.me/972524636767?text=${encodeURIComponent(translations[language].mobileBarWhatsappMessage)}`;

  return (
    <div className="slideshow-container" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Gradient overlay for text contrast */}
      <div className="slide-gradient-overlay" />

      {/* Static content overlay */}
      <div className="slide-content">
        <motion.img
          src={logoImage}
          alt="Logo"
          className="slide-logo"
          custom={0}
          initial="hidden"
          animate="visible"
          variants={heroTextVariants}
        />
       
        <motion.div custom={0.3} initial="hidden" animate="visible" variants={heroTextVariants}>
          <h1 className="slide-title">
            {translations[language].welcomeTitle}
          </h1>
        </motion.div>
        <motion.div custom={0.45} initial="hidden" animate="visible" variants={heroTextVariants}>
          <p className="slide-description">
            {translations[language].welcomeSubtitle}
          </p>
        </motion.div>
        <motion.div custom={0.6} initial="hidden" animate="visible" variants={heroTextVariants}>
          <Box mt={4} className="hero-cta-group">
            <Button
              variant="contained"
              size="large"
              className="hero-cta hero-cta--primary"
              component="a"
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              startIcon={<WhatsAppIcon />}
            >
              {translations[language].contactBtn}
            </Button>
            <Button
              variant="outlined"
              size="large"
              className="hero-cta hero-cta--secondary"
              onClick={handleScrollToPortfolio}
            >
              {translations[language].portfolioBtn}
            </Button>
          </Box>
        </motion.div>
      </div>

      <div className="scroll-hint" onClick={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}>
        <span>{translations[language].scrollHint}</span>
        <span className="scroll-hint__chevron" />
      </div>
      
      {/* Simplified carousel */}
      {images.length > 0 ? (
        <div className="carousel-container">
          <Carousel 
            showThumbs={false}
            showStatus={false}
            infiniteLoop={true}
            autoPlay={true}
            interval={4000}
            transitionTime={1000}
            animationHandler="fade"
            showArrows={true}
            showIndicators={true}
            swipeable={false}
            emulateTouch={false}
            stopOnHover={false}
            className="image-carousel"
          >
            {images.map((image, index) => (
              <div key={index} className="carousel-slide">                
                <img 
                  src={image} 
                  alt={`Slide ${index + 1}`}
                  className="carousel-image"
                />
              </div>
            ))}
          </Carousel>
        </div>
      ) : (
        <div className="no-images-fallback">
          <p className="no-images-text">
            No slideshow images available
          </p>
        </div>
      )}
    </div>
  );
};

export default WelcomeSlideshow;
